// @ts-nocheck
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { Crown, Users, Shield, Settings, UserPlus, UserCheck } from 'lucide-react';

interface User {
  id: string;
  email: string;
  full_name?: string;
  created_at: string;
  roles: string[];
  organizations: Array<{
    id: string;
    name: string;
    role: string;
  }>;
}

export default function SuperAdmin() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isAssignRoleModalOpen, setIsAssignRoleModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newRole, setNewRole] = useState('');
  const [newOrgId, setNewOrgId] = useState('');
  const [newOrgRole, setNewOrgRole] = useState('');

  const { isSuperAdmin } = usePermissions();
  const queryClient = useQueryClient();

  // Fetch all users with their roles and organizations
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['super-admin-users'],
    queryFn: async () => {
      // Get all users from auth
      const { data: authUsers, error: authError } = await supabase.auth.admin.listUsers();
      if (authError) throw authError;

      const enrichedUsers = await Promise.all(
        authUsers.users.map(async (user) => {
          // Get user roles
          const { data: rolesData } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id);

          // Get organization memberships
          const { data: orgData } = await supabase
            .from('org_memberships')
            .select(`
              role,
              organizations (
                id,
                name
              )
            `)
            .eq('user_id', user.id)
            .eq('active', true);

          return {
            id: user.id,
            email: user.email || '',
            full_name: user.user_metadata?.full_name,
            created_at: user.created_at,
            roles: rolesData?.map(r => r.role) || [],
            organizations: orgData?.map(o => ({
              id: o.organizations?.id || '',
              name: o.organizations?.name || '',
              role: o.role
            })) || []
          };
        })
      );

      return enrichedUsers;
    },
    enabled: isSuperAdmin()
  });

  // Fetch organizations for assignment
  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations-for-assignment'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, org_type')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data;
    },
    enabled: isSuperAdmin()
  });

  // Assign role mutation
  const assignRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const { error } = await supabase
        .from('user_roles')
        .insert([{
          user_id: userId,
          role: role as 'admin' | 'manager' | 'user' | 'rh' | 'financeiro' | 'super_admin',
          created_by: (await supabase.auth.getUser()).data.user?.id
        }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin-users'] });
      toast({
        title: "Role atribuído",
        description: "O role foi atribuído com sucesso."
      });
    }
  });

  // Assign organization membership mutation
  const assignOrgMutation = useMutation({
    mutationFn: async ({ userId, orgId, role }: { userId: string; orgId: string; role: string }) => {
      const { error } = await supabase
        .from('org_memberships')
        .insert([{
          user_id: userId,
          org_id: orgId,
          role,
          invited_by: (await supabase.auth.getUser()).data.user?.id,
          created_by: (await supabase.auth.getUser()).data.user?.id,
          updated_by: (await supabase.auth.getUser()).data.user?.id
        }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin-users'] });
      toast({
        title: "Usuário adicionado à organização",
        description: "O usuário foi adicionado à organização com sucesso."
      });
    }
  });

  const handleAssignRole = () => {
    if (!selectedUser || !newRole) return;

    assignRoleMutation.mutate({
      userId: selectedUser.id,
      role: newRole
    });

    setIsAssignRoleModalOpen(false);
    setSelectedUser(null);
    setNewRole('');
  };

  const handleAssignOrganization = () => {
    if (!selectedUser || !newOrgId || !newOrgRole) return;

    assignOrgMutation.mutate({
      userId: selectedUser.id,
      orgId: newOrgId,
      role: newOrgRole
    });

    setNewOrgId('');
    setNewOrgRole('');
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openAssignModal = (user: User) => {
    setSelectedUser(user);
    setIsAssignRoleModalOpen(true);
  };

  // Check permissions
  if (!isSuperAdmin()) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">Acesso Restrito</h3>
          <p className="text-muted-foreground">Apenas Super Admins têm acesso a este painel.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crown className="h-8 w-8 text-yellow-600" />
          <div>
            <h1 className="text-3xl font-bold">Super Admin Panel</h1>
            <p className="text-muted-foreground">
              Controle master de usuários, roles e organizações do sistema
            </p>
          </div>
        </div>
        
        <Badge variant="outline" className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200">
          <Crown className="h-3 w-3 mr-1 text-yellow-600" />
          Grupo Ares Master Control
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Usuários</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{users.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Super Admins</CardTitle>
            <Crown className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {users.filter(user => user.roles.includes('super_admin')).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Administradores</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {users.filter(user => user.roles.includes('admin')).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Organizações</CardTitle>
            <Settings className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{organizations.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle>Pesquisar Usuários</CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            placeholder="Buscar por email ou nome..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Gerenciar Usuários e Permissões</CardTitle>
          <CardDescription>
            Controle total sobre usuários, roles e membros de organizações
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Usuário</TableHead>
                <TableHead>Roles Sistema</TableHead>
                <TableHead>Organizações</TableHead>
                <TableHead>Cadastrado em</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{user.full_name || 'Sem nome'}</div>
                      <div className="text-sm text-muted-foreground">{user.email}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {user.roles.map((role) => (
                        <Badge 
                          key={role} 
                          variant={role === 'super_admin' ? 'default' : 'secondary'}
                          className={role === 'super_admin' ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' : ''}
                        >
                          {role === 'super_admin' && <Crown className="h-3 w-3 mr-1" />}
                          {role}
                        </Badge>
                      ))}
                      {user.roles.length === 0 && (
                        <span className="text-sm text-muted-foreground">Nenhum role</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {user.organizations.map((org) => (
                        <div key={org.id} className="flex items-center gap-2">
                           <Badge variant="outline">
                            {org.name}
                          </Badge>
                          <span className="text-xs text-muted-foreground">({org.role})</span>
                        </div>
                      ))}
                      {user.organizations.length === 0 && (
                        <span className="text-sm text-muted-foreground">Nenhuma organização</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {new Date(user.created_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openAssignModal(user)}
                      >
                        <UserPlus className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Assign Role Modal */}
      <Dialog open={isAssignRoleModalOpen} onOpenChange={setIsAssignRoleModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gerenciar Usuário</DialogTitle>
            <DialogDescription>
              Atribuir roles e organizações para {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Assign Role Section */}
            <div className="space-y-4">
              <Label className="text-base font-medium">Atribuir Role do Sistema</Label>
              <div className="flex gap-2">
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Selecionar role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="rh">RH</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleAssignRole} disabled={!newRole}>
                  <UserCheck className="h-4 w-4 mr-2" />
                  Atribuir Role
                </Button>
              </div>
            </div>

            {/* Assign Organization Section */}
            <div className="space-y-4">
              <Label className="text-base font-medium">Adicionar à Organização</Label>
              <div className="flex gap-2">
                <Select value={newOrgId} onValueChange={setNewOrgId}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Selecionar organização" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name} {org.org_type === 'master' && '👑'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={newOrgRole} onValueChange={setNewOrgRole}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                  </SelectContent>
                </Select>
                <Button 
                  onClick={handleAssignOrganization} 
                  disabled={!newOrgId || !newOrgRole}
                >
                  Adicionar
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAssignRoleModalOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}